import { Flex, Layout, spacing, useResponsiveValue } from '@lemonade-hq/uniclient-theme';
import { AnimatePresence, motion } from 'motion/react';
import type { PropsWithChildren } from 'react';
import { HistoryMessageBubble } from '../elements/HistoryMessageBubble';
import { COMPONENT_TITLE_MARGIN } from '../elements/WizardComponent.css';
import * as wizardHistoryStyles from '../elements/WizardHistory.css';
import { WizardHistoryOutput } from '../elements/WizardHistoryOutput';
import { OUTPUT_TRANSITION_DURATION } from './durations';

interface WizardComponentHistoryOutputAnimatorProps {
  readonly animate: boolean;
  readonly output?: string;
  readonly onTransitionEnd?: () => void;
}

export const WizardComponentHistoryOutputAnimator: React.FC<
  PropsWithChildren<WizardComponentHistoryOutputAnimatorProps>
> = ({ children, output: answer, animate, onTransitionEnd }) => {
  const historyOutputTranslateY = useResponsiveValue(wizardHistoryStyles.HISTORY_OUTPUT_TRANSLATE_Y);

  return (
    <AnimatePresence mode="popLayout">
      {animate ? (
        <Layout
          animate={{ opacity: 1, translateY: `-${historyOutputTranslateY}` }}
          as={motion.div}
          initial={{ opacity: 0, translateY: spacing.s32 }}
          key="output"
          mt={{
            sm: `calc(-${COMPONENT_TITLE_MARGIN.sm} + ${wizardHistoryStyles.HISTORY_TITLE_GAP})`,
            lg: `calc(-${COMPONENT_TITLE_MARGIN.lg} + ${wizardHistoryStyles.HISTORY_TITLE_GAP})`,
          }}
          onAnimationComplete={onTransitionEnd}
          transition={{
            type: 'keyframes',
            duration: OUTPUT_TRANSITION_DURATION / 1000,
            ease: [0.5, 0.5, 0.3, 0.8],
          }}
        >
          <Flex justifyContent="flex-end" width="100%">
            {answer != null && answer.trim() !== '' && (
              <HistoryMessageBubble value={answer}>
                <WizardHistoryOutput editVisible={false} showEdit={false} value={answer} />
              </HistoryMessageBubble>
            )}
          </Flex>
        </Layout>
      ) : (
        <Flex
          as={motion.div}
          exit={{ opacity: 0 }}
          flexDirection="column"
          height="100%"
          initial={{ opacity: 1 }}
          justifyContent="start"
          key="component"
          transition={{
            duration: OUTPUT_TRANSITION_DURATION / 1000,
          }}
        >
          {children}
        </Flex>
      )}
    </AnimatePresence>
  );
};
